import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SwapArrowIcon } from '~/components/icons'
import { TradeDirection } from '~/components/Exchange/types'
import TokenLogo from '~/components/TokenLogo'
import { useTokenUSDPriceWithAmount } from '~/hooks/useTokenPrice'
import { useModalOpened, useModalSwitch } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import {
  useLimitOrderUserConfig,
  useLimitSwapAmount,
  useLimitSwapPair,
} from '~/state/dex/hooks'
import { SpreadLabelLite } from '../SpreadLabel'
import TradeButton from '../TradeButton'
import ExpandedRateArea from '../ExpandedRateArea'
import { PreviewWarning } from './components/PreviewWarning'
import {
  BackButton,
  Content,
  StyledExpiredValue,
  StyledExpiredRow,
  StyledOrderPreview,
  Title,
  TokenAmount,
  TokenRow,
  TokenValue,
} from './styled'
import { formatFormAmount } from '~/utils/numbers'

const OrderPreview: React.FC = () => {
  const { t } = useTranslation()
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const isPreviewOpen = useModalOpened(ApplicationModal.LIMIT_ORDER_PREVIEW)
  const switchReview = useModalSwitch(ApplicationModal.LIMIT_ORDER_PREVIEW)

  // inputAmount 在输入时限制了为最多6位，不需要再 format
  const outputAmountLiteral = useMemo(() => {
    // @notice: outputAmount may be in scientific notation
    const [int, decimal] = outputAmount.toString().split('.')
    if (decimal && decimal.length > 6) return formatFormAmount(outputAmount)
    return outputAmount.toString()
  }, [outputAmount])

  const outputUSDValueLiteral = useTokenUSDPriceWithAmount(
    outputToken?.address,
    outputAmountLiteral,
  ) as string
  const inputUSDValueLiteral = useTokenUSDPriceWithAmount(
    inputToken?.address,
    inputAmount,
  ) as string
  const inputUSDValue = useTokenUSDPriceWithAmount(
    inputToken?.address,
    inputAmount,
    false,
  )
  const { expiration = '' } = useLimitOrderUserConfig()
  const [value, unit] = expiration.split('_')

  const onClose = () => {
    switchReview(false, { stack: true })
  }

  if (!isPreviewOpen) return null

  return (
    <StyledOrderPreview>
      <BackButton onClick={onClose}>
        <SwapArrowIcon />
      </BackButton>
      <Title>{t('preview_order')}</Title>
      <Content>
        <div>
          {inputUSDValue && Number(inputUSDValue) < 500 ? (
            <PreviewWarning />
          ) : null}
          <div className="border-bottom">
            <TokenRow>
              <TokenLogo
                logo={inputToken?.logo}
                symbol={inputToken?.symbol}
                space={0}
              />
              <div className="token-label">
                <TokenValue>~ {inputUSDValueLiteral}</TokenValue>
                <TokenAmount>
                  {inputAmount} {inputToken?.symbol}
                </TokenAmount>
              </div>
            </TokenRow>
            <SwapArrowIcon />
            <TokenRow>
              <TokenLogo
                logo={outputToken?.logo}
                symbol={outputToken?.symbol}
                space={0}
              />
              <div className="token-label">
                <TokenValue>
                  ~ {outputUSDValueLiteral}{' '}
                  <SpreadLabelLite direction={TradeDirection.Output} />
                </TokenValue>
                <TokenAmount>
                  {outputAmountLiteral} {outputToken?.symbol}
                </TokenAmount>
              </div>
            </TokenRow>
          </div>
          <ExpandedRateArea />
          <StyledExpiredRow>
            <span>{t('expired_time')}</span>
            <StyledExpiredValue>
              {value} {t(unit)}
            </StyledExpiredValue>
          </StyledExpiredRow>
        </div>
        <TradeButton />
      </Content>
    </StyledOrderPreview>
  )
}

export default OrderPreview
